import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Add } from '@material-ui/icons'
import { OTHER_CHARGES_INVOICE, strings } from '@constants'
import { Devices, Spacings, Text } from '@styles'
import { Accordion, DrawerBodyWrapper, DrawerFooterWrapper, SectionHeader, Toggle } from '@base'
import { displayAmount, getGstPercent } from '@utils'
import { isSupplyAdmin, isSuperAdmin } from 'src/utils/adminFilter'
import Summary from './Summary'

const Body = styled.div`
	display: flex;
	flex-direction: column;
`

const ToggleWrapper = styled.div`
	margin-left: auto;
`

const MathIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 ${Spacings.SPACING_2B};
	flex-grow: ${({ noGrow }) => (noGrow ? 0 : 1)};

	@media ${Devices.tablet} {
		flex-grow: 1;
	}

	& > svg {
		fill: ${({ theme }) => theme.palette.text.darkblack};
		font-size: ${Text.LARGE};
	}
`

const TotalAmount = styled.div`
	display: flex;
	align-items: center;
	flex-grow: 1;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_1B} 0;
	}
`

const SummaryItemCount = styled.div`
	display: flex;
	align-items: center;
	& > span {
		background: ${({ theme }) => theme.palette.background.blue};
		color: ${({ theme }) => theme.palette.text.white};
		font-size: ${Text.SMALL};
		width: 1.25rem;
		height: 1.25rem;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
	}
`

const TotalAmountInfo = styled.div`
	font-size: ${Text.MEDIUM};
	display: flex;
	flex-grow: 1;
	justify-content: center;
	flex-direction: column;

	& > span {
		margin-bottom: ${Spacings.SPACING_1B};
	}

	@media ${Devices.tablet} {
		flex-direction: row;
		align-items: center;
		justify-content: initial;
		& > span {
			margin-bottom: 0;
		}
	}
`

const SummaryItemValue = styled.div`
	font-size: ${({ big }) => (big ? Text.LARGE : Text.MEDIUM)};
	flex-basis: 35%;
	font-weight: ${({ big }) => (big ? Text.BOLD_600 : 'initial')};
	color: ${({ theme }) => theme.palette.text.darkblack};
`

const SummaryCalculation = styled.div`
	display: flex;
	flex-direction: row;
	color: ${({ theme }) => theme.palette.text.darkblack};
	& > span {
		margin: 0 ${Spacings.SPACING_1B};
	}
`

const AdminOrderSummary = ({ data }) => {
	const otherChargesInvoice = OTHER_CHARGES_INVOICE?.map((item) => item?.key)
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const role = user?.role
	const supplyOnly = isSupplyAdmin(role, false)
	const showToggle = isSuperAdmin(role)
	const [isPurchaseOrder, setIsPurchaseOrder] = useState(() => !supplyOnly)

	const { userData, weightKey, orderTotalAmount } = useMemo(() => {
		let userData
		let orderTotalAmount

		// const getServicedWeight = (invoices, item) => {
		// 	return invoices.reduce(
		// 		(total, _i) => {
		// 			let _weight = total.weight
		// 			if (_i.order_items) {
		// 				_weight += _i.order_items.find((_ix) => _ix.product_id === item.product_id).serviced_weight
		// 			}
		// 			return { weight: _weight }
		// 		},
		// 		{ weight: 0 }
		// 	).weight
		// }

		const getTotalItemAmount = (dataInvoices, productId) => {
			let avg = 0
			dataInvoices?.map((item) => {
				item?.order_items?.map((_i) => {
					if (productId == _i.product_id) {
						avg += _i?.price_per_kg * _i.serviced_weight ?? 0
					}
				})
			})
			return avg
		}

		// const getLogisticsValue = (_userData, _invoices, orderAmount = 0) => {
		// 	if (orderAmount === 0) return 0
		// 	const _totalWeight = _userData.order_items.reduce((_weight, _item) => _weight + getServicedWeight(_invoices, _item), 0)
		// 	return _userData.lumpsum_logistics_quote || (_userData.logistics_quote_per_mt * _totalWeight) / 1000 || 0
		// }

		const getOtherCharges = (_userData, _invoices, orderAmount = 0) => {
			if (orderAmount === 0) return 0
			return (
				_invoices
					?.map((element, i) =>
						Object.keys(element)
							?.map(
								(element2) =>
									otherChargesInvoice?.includes(element2) && element[element2] !== null && { [element2]: element[element2] }
							)
							?.filter((item) => item)
							?.reduce((a, b) => a + +Object.values(b), 0)
					)
					?.reduce((accumulator, currentValue) => accumulator + currentValue, 0) || 0
			)
		}

		if (isPurchaseOrder) {
			userData = data.buyer_list[0]
		} else {
			userData = data.supplier_list
		}

		const weightKey = isPurchaseOrder ? 'ordered_weight' : 'allotted_weight'
		const invoiceKey = isPurchaseOrder ? 'buyer_invoices' : 'supplier_invoices'
		let _itemsAmount = 0
		// let _logistics = 0
		let _totalOtherCharges = 0
		// items use their own gst (0 included, missing → 18%); other charges stay at 18%
		let _itemsGst = 0
		const getItemsGst = (_orderItems, _invoices) =>
			_orderItems.reduce(
				(_amount, _item) => _amount + (getTotalItemAmount(_invoices, _item?.product_id) * getGstPercent(_item?.gst)) / 100,
				0
			)

		if (Array.isArray(userData)) {
			userData.forEach((_supplier) => {
				const invoices = _supplier[invoiceKey] || []
				const _itemAmount = _supplier.order_items.reduce((_amount, _item) => _amount + getTotalItemAmount(invoices, _item?.product_id), 0)
				// _logistics += getLogisticsValue(_supplier, invoices, _itemAmount)
				_itemsAmount += _itemAmount
				_itemsGst += getItemsGst(_supplier.order_items, invoices)
				_totalOtherCharges += getOtherCharges(_supplier, invoices, _itemAmount)
			})
		} else {
			const invoices = userData[invoiceKey] || []
			_itemsAmount = userData.order_items.reduce((_amount, _item) => _amount + getTotalItemAmount(invoices, _item?.product_id), 0)
			_itemsGst = getItemsGst(userData.order_items, invoices)
			// _logistics = getLogisticsValue(userData, invoices, _itemsAmount)
			_totalOtherCharges = getOtherCharges(userData, invoices, _itemsAmount)
		}

		// orderTotalAmount = (_itemsAmount + _logistics) * 1.18
		orderTotalAmount = _itemsAmount + _totalOtherCharges + _itemsGst + _totalOtherCharges * 0.18

		return {
			userData,
			weightKey,
			orderTotalAmount,
		}
	}, [data, isPurchaseOrder])

	const renderTotalAmount = () => {
		return (
			<>
				<TotalAmount>
					<TotalAmountInfo>
						<span>{strings('total_amount')}</span>
						{isPurchaseOrder && (
							<SummaryCalculation>
								<span>(</span>
								<SummaryItemCount>
									<span>1</span>
								</SummaryItemCount>
								<MathIcon noGrow>
									<Add />
								</MathIcon>
								<SummaryItemCount>
									<span>2</span>
								</SummaryItemCount>
								<MathIcon noGrow>
									<Add />
								</MathIcon>
								<SummaryItemCount>
									<span>3</span>
								</SummaryItemCount>
								<span>)</span>
							</SummaryCalculation>
						)}
					</TotalAmountInfo>
					<SummaryItemValue big>{displayAmount(orderTotalAmount)}</SummaryItemValue>
				</TotalAmount>
			</>
		)
	}

	const toggleClick = (flag) => () => setIsPurchaseOrder(flag)

	const renderSummary = () => {
		if (Array.isArray(userData)) {
			return userData.map((_data, index) => {
				return (
					<Accordion header={_data?.supplier.full_name ?? _data?.supplier.user_id} key={`accordion-summary-${index}`}>
						<Summary data={_data} weightKey={weightKey} showTotal otherChargesInvoice={otherChargesInvoice} />
					</Accordion>
				)
			})
		}
		return <Summary data={userData} weightKey={weightKey} otherChargesInvoice={otherChargesInvoice} />
	}

	return (
		<>
			<DrawerBodyWrapper>
				{showToggle && (
					<SectionHeader>
						<ToggleWrapper>
							<Toggle
								options={[
									{
										label: strings('buyer'),
										click: toggleClick(true),
										active: isPurchaseOrder,
									},
									{
										label: strings('suppliers'),
										click: toggleClick(false),
										active: !isPurchaseOrder,
									},
								]}
							/>
						</ToggleWrapper>
					</SectionHeader>
				)}
				<Body>{renderSummary()}</Body>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>{renderTotalAmount()}</DrawerFooterWrapper>
		</>
	)
}

export default AdminOrderSummary
